import React from 'react';
import ReactMarkdown from 'react-markdown';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { BsPencil } from '@react-icons/all-files/bs/BsPencil';
import remarkGfm from 'remark-gfm';

import { EditorPlaceholder } from '../../components/EditorPlaceholder';

export interface TextProps {
  size: number;
  color: string;
  weight: 'lighter' | 500 | 600;
  align: 'left' | 'center' | 'right';
  background: string;
  value?: string;
}

export function Text({ value, ...props }: TextProps) {
  if (!value) {
    return (
      <EditorPlaceholder icon={<BsPencil />} message="내용을 입력해주세요." />
    );
  }

  return (
    <Container {...props}>
      { value && (
        <ReactMarkdown remarkPlugins={[remarkGfm]}>{ value }</ReactMarkdown>
      ) }
    </Container>
  );
}

const Container = styled.div<Omit<TextProps, 'value'>>`
  font-size: ${({ size }) => size}px;
  color: ${({ color }) => color};
  font-weight: ${({ weight }) => weight};
  text-align: ${({ align }) => align};
  background-color: ${({ background }) => background};
  word-break: break-all;
  ${Array(6)
    .fill(1)
    .map(
      (_, i) => css`
        h${i + 1} {
          font-weight: bolder;
          font-size: ${1.6 - (i + 1) / 10}em;
        }
      `,
    )}
  * {
    margin: 0;
    padding: 0;
    line-height: 1.4;
    &:first-of-type {
      margin-top: 0;
    }
    &:last-of-type {
      margin-bottom: 0;
    }
    &:first-of-type:last-of-type {
      margin: 0;
    }
  }
  a {
    color: inherit;
    text-decoration: underline;
  }
  del {
    text-decoration: line-through;
  }
  em {
    font-style: italic;
  }
  strong {
    font-weight: bolder;
  }
  blockquote {
    position: relative;
    margin: 0.5em 0;
    padding-left: 1em;
    &:before {
      content: '';
      position: absolute;
      top: 0.1em;
      left: 0.3em;
      bottom: 0.1em;
      width: 0.3em;
      background: rgba(0, 0, 0, 0.1);
    }
  }
  ul,
  ol {
    list-style: none;
    margin: 0.5em 0;
    li {
      position: relative;
      display: block;
      padding-left: 1em;
      &:before {
        position: absolute;
        top: 0;
        left: 0.3em;
      }
    }
  }
  ul li:before {
    content: '-';
  }
  ol {
    counter-reset: ol;
    li:before {
      counter-increment: ol;
      content: counter(ol) '.';
      left: 0;
    }
  }
  hr {
    margin: 0.5em 0;
    height: 1px;
    background: rgba(0, 0, 0, 0.1);
  }
  table {
    margin: 0.5em 0;
    table-layout: fixed;
    width: 100%;
    border-collapse: collapse;
    border-spacing: 0;
    th,
    td {
      padding: 5px;
      border: 1px solid rgba(0, 0, 0, 0.1);
    }
    th {
      font-weight: bolder;
    }
  }
  img {
    width: 100%;
    padding: 10px;
  }
  pre {
    margin: 0.5em 0;
    padding: 10px;
    border-radius: 4px;
    background: rgba(0, 0, 0, 0.05);
  }
`;
